import { useMemo, useState } from 'react';
import {
  Users,
  Copy,
  Check,
  Shield,
  Crown,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

import './clan.css';

import {
  clearClanInvitation,
  useAuth,
  useClanMembers,
  useIsClanMember,
  useUserData,
} from '../../hooks';
import { joinClan } from '../../firebase';
import { Header } from '../../components';
import { ROUTES } from '../../routes';

export const Clan = () => {
  const { user } = useAuth();
  const { userData } = useUserData();
  const { hasJoined } = useIsClanMember();
  const clanRootUid = userData?.uid;

  const { members } = useClanMembers(clanRootUid, user?.uid);

  const [copiado, setCopiado] = useState(false);
  const [mostrarInstrucciones, setMostrarInstrucciones] = useState(false);

  const leaderName = useMemo(() => {
    if (!userData) return '';

    if (userData.isClanRoot) {
      return `${userData.name ?? ''} ${userData.lastName ?? ''}`.trim();
    }

    return userData.parentName ?? '';
  }, [userData]);

  const referralCode = userData?.referralCode ?? '';
  const urlReferralCode = `https://imperialstonesgame.com/?${referralCode}`;

  const copiarCodigo = () => {
    if (!referralCode) return;

    navigator.clipboard.writeText(urlReferralCode);

    setCopiado(true);

    setTimeout(() => setCopiado(false), 2000);
  };

  return (
    <div className="clan-page">
      <Header goBack={{ path: ROUTES.HOME }} title="Mi Clan Imperial" />

      <div className="clan-container">
        {!hasJoined ? (
          <ClanJoin />
        ) : (
          <div className="clan-stack">
            <div className="clan-card purple">
              <div className="clan-flex">
                <div className="clan-avatar purple">
                  <Crown size={32} stroke="#FFFFFF" />
                </div>
                <div>
                  <p className="small">Tu Referidor (Líder)</p>
                  <h2>{leaderName}</h2>
                </div>
              </div>

              <div className="clan-box cyan-border">
                <p>
                  <span className="cyan-text">{leaderName}</span> es tu líder de
                  clan. Cada vez que ganas en una liga, él también recibe
                  beneficios como recompensa por ser tu referidor.
                </p>
              </div>
            </div>

            <div className="clan-card green">
              <div className="clan-flex">
                <div className="clan-avatar green">
                  <Users size={32} stroke="#000011" />
                </div>
                <div>
                  <p className="small">Tu Código de Referido</p>
                  <h2>Construye Tu Imperio</h2>
                </div>
              </div>

              <p className="clan-text">
                Comparte este código con tus amigos para que se unan a tu clan.
                Por cada victoria de tus referidos, recibirás recompensas
                adicionales.
              </p>

              <div className="clan-code-row">
                <div className="clan-code">{referralCode}</div>

                <button onClick={copiarCodigo} className="clan-copy">
                  {copiado ? (
                    <>
                      <Check size={20} stroke="#000011" />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy size={20} stroke="#000011" />
                      Copiar
                    </>
                  )}
                </button>
              </div>

              <button
                onClick={() => setMostrarInstrucciones(!mostrarInstrucciones)}
                className="clan-toggle"
              >
                {mostrarInstrucciones ? (
                  <ChevronUp size={16} stroke="#CAFB10" />
                ) : (
                  <ChevronDown size={16} stroke="#CAFB10" />
                )}
                Cómo compartir tu código
              </button>

              {mostrarInstrucciones && (
                <div className="clan-box green-border">
                  <ol className="clan-instructions-ol">
                    <li>Copia tu código usando el botón "Copiar"</li>
                    <li>
                      Compártelo con tus amigos por WhatsApp, redes sociales,
                      etc.
                    </li>
                    <li>
                      Cuando se registren, deberán ingresar tu código para
                      unirse a tu clan
                    </li>
                    <li>¡Recibirás beneficios cada vez que ellos ganen!</li>
                  </ol>
                </div>
              )}
            </div>

            <div className="clan-card cyan">
              <div className="clan-flex space-between">
                <div>
                  <h2>Tus Aliados ({members.length})</h2>
                  <p className="small">
                    Jugadores que se unieron usando tu código
                  </p>
                </div>

                <div className="clan-badge">{members.length}</div>
              </div>

              {members.length > 0 ? (
                <div className="clan-list">
                  {members.map((miembro) => {
                    const parts = (miembro.name || '').trim().split(' ');
                    const nombre = parts[0] || '';
                    const apellido = parts.slice(1).join(' ');

                    return (
                      <div key={miembro.uid} className="clan-member">
                        <div className="clan-member-left">
                          <div className="clan-avatar small cyan">
                            <Users size={20} stroke="#000011" />
                          </div>

                          <div>
                            <p className="bold">
                              {nombre} {apellido}
                            </p>
                            <p className="tiny">Miembro del clan</p>
                          </div>
                        </div>

                        <Shield size={20} stroke="#90F7FF" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="clan-empty">
                  <Users size={64} stroke="#4B5563" />
                  <p>
                    Aún no tienes aliados en tu clan. ¡Comparte tu código para
                    empezar a construir tu imperio!
                  </p>
                </div>
              )}
            </div>

            <div className="clan-privacy">
              🔒 Solo se muestra el nombre y apellido de tus aliados por
              protección de datos. Información de contacto y estadísticas
              detalladas permanecen privadas.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ClanJoin = ({ code }: { code?: string }) => {
  const { user } = useAuth();

  const [codigoUnirse, setCodigoUnirse] = useState(code ?? '');
  const [joining, setJoining] = useState(false);

  const unirseAClan = async () => {
    if (!user?.uid || !codigoUnirse.trim()) return;

    try {
      setJoining(true);

      await joinClan({
        uid: user.uid,
        referralCode: codigoUnirse.trim().toUpperCase(),
      });

      setCodigoUnirse('');
      alert('Te has unido al clan correctamente');
    } catch (error: any) {
      alert(error?.message || 'No se pudo unir al clan');
    } finally {
      setJoining(false);
      clearClanInvitation();
    }
  };

  return (
    <div className="clan-stack">
      <div className="clan-card purple">
        <div className="clan-flex-start">
          <Shield size={48} stroke="#5025E1" />
          <div>
            <h2>Únete a un Clan Imperial</h2>
            <p>
              En Imperial Stones Game, los clanes son alianzas permanentes que
              te vinculan con otros jugadores. Al unirte usando el código de un
              referidor, te conviertes en su aliado y él obtendrá beneficios
              cada vez que ganes.
            </p>
          </div>
        </div>

        <div className="clan-grid">
          <div className="clan-mini cyan">
            <div className="clan-mini-header">
              <Users size={20} stroke="#90F7FF" />
              <h3>Beneficios para ti</h3>
            </div>
            <p>
              Tu referidor obtiene recompensas cuando tú ganas, fortaleciendo
              vuestra alianza.
            </p>
          </div>

          <div className="clan-mini green">
            <div className="clan-mini-header">
              <Crown size={20} stroke="#CAFB10" />
              <h3>Crea tu línea</h3>
            </div>
            <p>
              Comparte tu código para que otros se unan a tu clan y recibir
              beneficios.
            </p>
          </div>
        </div>

        <div className="clan-warning">
          <p>
            <span>⚠️ Importante:</span> Una vez que te unes a un clan, esta
            decisión es permanente e inmodificable. Elige sabiamente tu alianza.
          </p>
        </div>
      </div>

      <div className="clan-card cyan">
        <h3>Unirse a un Clan</h3>
        <p>
          Introduce el código de referido de tu aliado para unirte a su clan.
        </p>

        <div className="clan-form">
          <input
            type="text"
            value={codigoUnirse}
            onChange={(e) => setCodigoUnirse(e.target.value.toUpperCase())}
            placeholder="ISG-XXX-XXXX"
            className="clan-input"
          />

          <button
            onClick={unirseAClan}
            disabled={codigoUnirse.length < 8 || joining}
            className="clan-btn"
          >
            {joining ? 'Uniéndose...' : 'Unirse al Clan'}
          </button>
        </div>
      </div>

      <div className="clan-card gray center">
        <p>¿No tienes un código de referido?</p>
        <p className="small">
          Puedes jugar sin clan, pero te perderás los beneficios de las alianzas
          imperiales. Busca aliados en la comunidad.
        </p>
      </div>
    </div>
  );
};
